import { supabase } from "@/integrations/supabase/client";
import { ClientService } from "./ClientService";

/**
 * Service to manage the relationship between invoices and client credit
 */
export class InvoiceCreditService {
  /**
   * Handle invoice creation - update client credit if applicable
   */
  static async handleInvoiceCreated(
    invoiceId: string,
    clientId: string | null,
    totalAmount: number,
    repairShopId: string,
    createdBy: string
  ): Promise<void> {
    if (!clientId) return; // No client associated with invoice

    try {
      // Create credit transaction for the new invoice
      await ClientService.createCreditTransaction(
        clientId,
        "credit",
        totalAmount,
        "invoice",
        invoiceId,
        `Invoice created: ${invoiceId}`,
        undefined,
        repairShopId,
        createdBy
      );

      // Update client's total credit balance
      await ClientService.updateClientCreditFromInvoices(clientId);
    } catch (error) {
      console.error(
        "Error handling invoice creation for client credit:",
        error
      );
      // Don't throw error to avoid breaking invoice creation
    }
  }

  /**
   * Handle invoice payment - update client credit
   */
  static async handleInvoicePayment(
    invoiceId: string,
    clientId: string | null,
    paymentAmount: number,
    paymentMethod: string,
    repairShopId: string,
    createdBy: string
  ): Promise<void> {
    if (!clientId) return; // No client associated with invoice

    try {
      // Create credit transaction for the payment
      await ClientService.createCreditTransaction(
        clientId,
        "payment",
        paymentAmount,
        "payment",
        invoiceId,
        `Payment received for invoice: ${invoiceId}`,
        paymentMethod,
        repairShopId,
        createdBy
      );

      // Update client's total credit balance
      await ClientService.updateClientCreditFromInvoices(clientId);
    } catch (error) {
      console.error("Error handling invoice payment for client credit:", error);
      // Don't throw error to avoid breaking payment processing
    }
  }

  /**
   * Handle invoice status change - update client credit if needed
   */
  static async handleInvoiceStatusChange(
    invoiceId: string,
    clientId: string | null,
    oldStatus: string,
    newStatus: string,
    totalAmount: number,
    repairShopId: string,
    createdBy: string
  ): Promise<void> {
    if (!clientId) return; // No client associated with invoice

    try {
      // If invoice is cancelled, remove it from client credit
      if (newStatus === "cancelled" && oldStatus !== "cancelled") {
        await ClientService.createCreditTransaction(
          clientId,
          "payment",
          totalAmount,
          "invoice",
          invoiceId,
          `Invoice cancelled: ${invoiceId}`,
          undefined,
          repairShopId,
          createdBy
        );
      }

      // If invoice is reactivated from cancelled, add it back to credit
      if (oldStatus === "cancelled" && newStatus !== "cancelled") {
        await ClientService.createCreditTransaction(
          clientId,
          "credit",
          totalAmount,
          "invoice",
          invoiceId,
          `Invoice reactivated: ${invoiceId}`,
          undefined,
          repairShopId,
          createdBy
        );
      }

      // Update client's total credit balance
      await ClientService.updateClientCreditFromInvoices(clientId);
    } catch (error) {
      console.error(
        "Error handling invoice status change for client credit:",
        error
      );
      // Don't throw error to avoid breaking invoice updates
    }
  }

  /**
   * Handle invoice amount change - update client credit
   */
  static async handleInvoiceAmountChange(
    invoiceId: string,
    clientId: string | null,
    oldAmount: number,
    newAmount: number,
    repairShopId: string,
    createdBy: string
  ): Promise<void> {
    if (!clientId) return; // No client associated with invoice

    try {
      const amountDifference = newAmount - oldAmount;

      if (amountDifference !== 0) {
        await ClientService.createCreditTransaction(
          clientId,
          amountDifference > 0 ? "credit" : "payment",
          Math.abs(amountDifference),
          "invoice",
          invoiceId,
          `Invoice amount ${
            amountDifference > 0 ? "increased" : "decreased"
          }: ${invoiceId}`,
          undefined,
          repairShopId,
          createdBy
        );

        // Update client's total credit balance
        await ClientService.updateClientCreditFromInvoices(clientId);
      }
    } catch (error) {
      console.error(
        "Error handling invoice amount change for client credit:",
        error
      );
      // Don't throw error to avoid breaking invoice updates
    }
  }

  /**
   * Get credit summary for a client
   */
  static async getClientCreditSummary(clientId: string): Promise<{
    currentBalance: number;
    creditLimit: number;
    availableCredit: number;
    totalInvoiced: number;
    totalPaid: number;
    unpaidInvoices: number;
    recentTransactions: any[];
  }> {
    try {
      // Get client info
      const client = await ClientService.getClientById(clientId);
      if (!client) {
        throw new Error("Client not found");
      }

      // Get invoice summary
      const invoiceSummary = await ClientService.getClientInvoiceSummary(
        clientId
      );

      // Get recent credit transactions
      const recentTransactions = await ClientService.getCreditTransactions(
        clientId
      );

      // Calculate totals
      const totalPaid =
        invoiceSummary.totalAmount - invoiceSummary.unpaidAmount;
      const availableCredit = Math.max(
        0,
        client.creditLimit - client.currentCreditBalance
      );

      return {
        currentBalance: client.currentCreditBalance,
        creditLimit: client.creditLimit,
        availableCredit,
        totalInvoiced: invoiceSummary.totalAmount,
        totalPaid,
        unpaidInvoices: invoiceSummary.unpaidInvoices,
        recentTransactions: recentTransactions.slice(0, 10), // Last 10 transactions
      };
    } catch (error) {
      console.error("Error getting client credit summary:", error);
      throw error;
    }
  }

  /**
   * Check if a new invoice would exceed client's credit limit
   */
  static async validateInvoiceAgainstCreditLimit(
    clientId: string,
    invoiceAmount: number
  ): Promise<{
    allowed: boolean;
    currentBalance: number;
    creditLimit: number;
    newBalance: number;
    exceedsBy: number;
  }> {
    try {
      const client = await ClientService.getClientById(clientId);
      if (!client) {
        throw new Error("Client not found");
      }

      const newBalance = client.currentCreditBalance + invoiceAmount;
      const exceedsBy = Math.max(0, newBalance - client.creditLimit);
      const allowed = newBalance <= client.creditLimit;

      return {
        allowed,
        currentBalance: client.currentCreditBalance,
        creditLimit: client.creditLimit,
        newBalance,
        exceedsBy,
      };
    } catch (error) {
      console.error("Error validating invoice against credit limit:", error);
      throw error;
    }
  }

  /**
   * Manually adjust client credit balance
   */
  static async adjustClientCredit(
    clientId: string,
    adjustmentAmount: number,
    reason: string,
    repairShopId: string,
    createdBy: string
  ): Promise<void> {
    try {
      await ClientService.createCreditTransaction(
        clientId,
        "adjustment",
        adjustmentAmount,
        "manual",
        undefined,
        reason,
        undefined,
        repairShopId,
        createdBy
      );
    } catch (error) {
      console.error("Error adjusting client credit:", error);
      throw error;
    }
  }
}
